import Image from "next/image";
import React from "react";
import { usePropertyContext } from "../../context";
import ExemptionsAnimationWhite from "public/account/exemptions/exemptions-animation-white.gif";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { useExemptionsContext } from "../../ExemptionsContext";
import Script from "next/script";
import { useAuthContext } from "context/AuthContext";
import ButtonV2 from "elements/ButtonV2";
import { formatRelativeUrl } from "utils/formatRelativeUrl";

const MainConfirmation = ({ ExitButton }: { ExitButton?: React.FC }) => {
	const { userData } = useAuthContext();
	const { setModalToShow, details, fetchPropertyDetails } =
		usePropertyContext();
	const {
		flowId,
		exemptionId,
		additionalOwnerFirstNameValue,
		owner2DlFilesToUpload,
		owner2DlVerificationErrored,
		ownerDlFilesToUpload,
		ownerDlVerificationErrored,
	} = useExemptionsContext();
	const handleSubmit = async () => {
		setModalToShow(undefined);
		onAttemptExit();
	};

	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const onAttemptExit = () => {
		if (!details) return;
		setModalToShow(undefined);
		router.push(
			formatRelativeUrl({
				pathname: pathname.replace(/\/exemptions$/, ""),
				query: {
					...(searchParams.get("onboarding") ? { onboarding: true } : {}),
				},
			})
		);
		fetchPropertyDetails(details.id.toString(), {
			autoOpenNARModal: false,
		}).catch(err => {
			console.error(err);
			sendErrorToWebhook(
				"Error caught in onAttemptExit of Exemptions Survey at the We Go it from here Page: " +
					err
			);
		});
	};

	const timestamp = Date.now();

	const showDriverLicenseMessage = React.useMemo(() => {
		if (
			flowId === "RESEARCH_EXEMPTION" ||
			flowId === "PENNSYLVANIA_EXEMPTION" ||
			flowId === "CALIFORNIA_EXEMPTION" ||
			flowId === "NEW_YORK_EXEMPTION" ||
			(details?.state === "GA" &&
				!["Fulton", "DeKalb"].includes(details?.county))
		) {
			return null;
		}
		if (!details) return null;
		const exemption = details?.exemption;
		if (
			additionalOwnerFirstNameValue &&
			((details?.state === "TX" &&
				!!details?.exemption_requires_both_licenses) ||
				(details?.state === "GA" &&
					["Fulton", "DeKalb"].includes(details?.county)) ||
				details?.state === "MI") &&
			!(
				exemption?.owner2_license_ref ||
				owner2DlFilesToUpload.length > 0 ||
				owner2DlVerificationErrored !== undefined
			)
		) {
			return "owner2";
		} else if (
			!(
				exemption?.owner1_license_ref ||
				ownerDlFilesToUpload.length > 0 ||
				ownerDlVerificationErrored !== undefined
			)
		) {
			return "owner1";
		}

		return null;
	}, [
		additionalOwnerFirstNameValue,
		details,
		ownerDlVerificationErrored,
		owner2DlVerificationErrored,
		owner2DlFilesToUpload,
		ownerDlFilesToUpload,
		flowId,
	]);

	return (
		<div>
			<div className="exemptions-question-card mt-4">
				<div className="exemptions-intro-animation-box-reversed mt-2">
					<div>
						<p className="lg bold">We’ve got it from here!</p>
						<p className="sm">
							Thanks for completing the exemptions application! We will reach
							out if we need any additional information or materials
							from&nbsp;you.
						</p>
					</div>
					<div className="exemptions-intro-animation">
						<Image
							src={ExemptionsAnimationWhite}
							unoptimized
							alt="Exemptions Table"
							style={{
								maxWidth: "100%",
								height: "auto",
							}}
						/>
					</div>
				</div>
				{["TEXAS_EXEMPTION", "GEORGIA_EXEMPTION"].includes(flowId ?? "") && (
					<p className="sm mt-2 mb-2">
						It can take the county anywhere from 12-16 weeks to review and
						implement any exemptions. We'll keep you updated every step of the
						way and notify you as soon as we have any updates to share.
					</p>
				)}
				{flowId === "CALIFORNIA_EXEMPTION" && (
					<p className="sm mt-2 mb-2">
						It can take the county anywhere anywhere from 6-12 months to review
						and implement any exemptions. We'll keep you updated every step of
						the way and notify you as soon as we have any updates to share.
					</p>
				)}
				{!!showDriverLicenseMessage && (
					<div className="exemptions-terracotta-box">
						<p className="sm bold">
							Don’t forget to upload a driver’s license!
						</p>
						<p className="sm">
							We will not be able to file for tax exemptions until a copy of{" "}
							{showDriverLicenseMessage === "owner2"
								? "all driver's licenses "
								: "your driver's license "}
							with a mailing address matching the property address is uploaded.
						</p>
					</div>
				)}
			</div>
			{ExitButton ? (
				<ExitButton />
			) : (
				<ButtonV2
					size="large"
					id="account-property-profile-intro-begin"
					onClick={handleSubmit}>
					Finish
				</ButtonV2>
			)}
			{exemptionId && (
				<>
					<Script id="exemptions-form-flow-complete-event">
						{`
										gtag('event', 'conversion', {
											'send_to': 'AW-622658771/Y0wCCKjH5-4YENOJ9KgC',
											'value': 1.0,
											'currency': 'USD',
											'transaction_id': '${exemptionId}'
										});
										gtag('event', 'exemptions_form_flow_complete')
									`}
					</Script>
					<Script id="bing-exemptions-form-flow-complete-event">
						{`
										window.uetq = window.uetq || [];
										window.uetq.push("event", "exemptions_submit_lead_form", {
											'transaction_id': "${exemptionId}",
											'pid': {
												'em': "${userData?.email}",
												'ph': "${userData?.phone}",
											},
										});
									`}
					</Script>
					<img src={`https://bidagent.xad.com/conv/260180?ts=${timestamp}`} />
				</>
			)}
		</div>
	);
};

export default MainConfirmation;
